
import React from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/components/ui/use-toast";

// Types of tests that can be favorited
export type TestType = "level1" | "level2" | "level3" | "putting" | "approach" | "chipping" | "wedging";

interface FavoriteButtonProps extends React.ComponentPropsWithoutRef<typeof Button> {
  testId: string;
  testType: TestType;
  refreshAfterToast?: () => void;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({ 
  testId, 
  testType, 
  refreshAfterToast, 
  ...buttonProps 
}) => {
  const { toast } = useToast();
  const { favorites, addFavorite, removeFavorite, isLoading } = useFavorites();
  
  const isFavorited = favorites.some(fav => fav.test_id === testId && fav.test_type === testType);
  
  const handleToggleFavorite = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (isLoading) return;
    
    try {
      if (isFavorited) {
        // Find the favorite to remove
        const favoriteToRemove = favorites.find(fav => 
          fav.test_id === testId && fav.test_type === testType
        );
        
        if (favoriteToRemove) {
          await removeFavorite(favoriteToRemove.id);
          
          toast({
            title: "Retiré des favoris",
            description: "Le test a été retiré de vos favoris",
          });
        }
      } else {
        await addFavorite(testId, testType);
        
        toast({
          title: "Ajouté aux favoris",
          description: "Le test a été ajouté à vos favoris",
        });
      }
      
      // If a refresh callback is provided, call it
      if (refreshAfterToast) {
        setTimeout(refreshAfterToast, 100);
      }
      
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la modification des favoris",
      });
    }
  };
  
  return (
    <Button
      onClick={handleToggleFavorite}
      disabled={isLoading}
      {...buttonProps}
    >
      <Star 
        className={isFavorited ? "fill-amber-500 text-amber-500" : "text-gray-400"} 
      />
      {buttonProps.children}
    </Button>
  );
};

export default FavoriteButton;
